import { useEffect, useState } from "react";
import { X, BarChart3, Hash, ShieldCheck } from "lucide-react";
import { authService } from "../../services/api";
import Loading from "../../components/UI/Loading/Loading";

const BRL = (v) =>
    new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

const num = (v) => new Intl.NumberFormat("pt-BR").format(v || 0);

function fmtDate(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleString("pt-BR", {
        day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
    });
}

function deviceFromUA(ua) {
    if (!ua) return "Desconhecido";
    return /mobi|android|iphone|ipad/i.test(ua) ? "Mobile" : "Desktop";
}

function StatTile({ label, value, accent }) {
    return (
        <div className="rounded-xl border border-white/[0.08] bg-white/[0.03] p-3 md:p-4 min-w-0">
            <div className="text-[10px] md:text-xs uppercase tracking-wide text-white/40">{label}</div>
            <div className={`mt-1 text-xl md:text-2xl font-bold truncate ${accent || "text-white"}`}>
                {value}
            </div>
        </div>
    );
}

function FunnelBar({ label, value, total, color }) {
    const pct = total > 0 ? Math.max(2, Math.round((value / total) * 100)) : 0;
    return (
        <div className="mb-3">
            <div className="flex justify-between text-sm mb-1">
                <span className="text-white/70">{label}</span>
                <span className="font-semibold text-white">{num(value)}</span>
            </div>
            <div className="h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
            </div>
        </div>
    );
}

export default function StatsModal({ open, slug, siteUrl, onClose }) {
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);

    useEffect(() => {
        if (!open || !slug) return;
        let mounted = true;
        setLoading(true);
        setStats(null);
        authService.redirects
            .get_stats(slug)
            .then((res) => {
                if (mounted && res.data?.success) setStats(res.data.data);
            })
            .catch(() => { })
            .finally(() => mounted && setLoading(false));
        return () => { mounted = false; };
    }, [open, slug]);

    if (!open) return null;

    const s = stats;
    const funnelTotal = s ? Math.max(s.cliques, 1) : 1;

    return (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center p-2 md:p-4">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
            <div
                className="relative w-full max-w-4xl rounded-2xl border border-white/[0.09] bg-[#10141a] p-4 md:p-6 shadow-[0_24px_64px_rgba(0,0,0,0.55)] text-white"
                style={{ maxHeight: "92vh", overflowY: "auto", colorScheme: "dark" }}
            >
                {/* Header */}
                <div className="flex items-start justify-between mb-5 gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-[rgba(139,92,246,0.12)] border border-[rgba(139,92,246,0.18)] flex items-center justify-center text-[#a78bfa]">
                            <BarChart3 size={20} />
                        </div>
                        <div className="min-w-0">
                            <h2 className="text-lg font-semibold">Relatório de Performance</h2>
                            <div className="text-sm text-[#a78bfa] truncate">{siteUrl}</div>
                        </div>
                    </div>
                    <button onClick={onClose} className="action-btn flex-shrink-0" aria-label="Fechar">
                        <X size={18} />
                    </button>
                </div>

                {loading || !s ? (
                    <Loading text="Carregando métricas..." />
                ) : (
                    <>
                        {/* Top stat tiles */}
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
                            <StatTile label="Cliques" value={num(s.cliques)} />
                            <StatTile label="Vendas" value={num(s.vendas)} />
                            <StatTile label="Receita" value={BRL(s.receita)} accent="text-[#4ade80]" />
                            <StatTile label="Conversão" value={`${s.conversao_pct}%`} accent="text-[#60a5fa]" />
                            <StatTile label="Ticket médio" value={BRL(s.ticket_medio)} />
                        </div>

                        {/* Funil + Atribuição */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5">
                            <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-4">
                                <div className="flex items-center justify-between mb-4">
                                    <span className="text-sm font-semibold flex items-center gap-2">
                                        <BarChart3 size={15} /> Funil do link
                                    </span>
                                    <span className="text-xs text-white/40">{BRL(s.receita)} total</span>
                                </div>
                                <FunnelBar label="Cliques recebidos" value={s.cliques} total={funnelTotal} color="#a78bfa" />
                                <FunnelBar label="Aprovados no cloaker" value={s.aprovados} total={funnelTotal} color="#4ade80" />
                                <FunnelBar label="Bloqueados" value={s.bloqueados} total={funnelTotal} color="#f87171" />
                                <FunnelBar label="Vendas atribuídas" value={s.vendas} total={funnelTotal} color="#60a5fa" />
                            </div>

                            <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-4">
                                <span className="text-sm font-semibold flex items-center gap-2 mb-4">
                                    <Hash size={15} /> Atribuição
                                </span>
                                <div className="space-y-3 text-sm">
                                    <div>
                                        <div className="text-xs uppercase tracking-wide text-white/40">Parâmetro</div>
                                        <div className="mt-0.5">
                                            {s.atribuicao.parametro
                                                ? <code className="text-[#fbbf24]">sx={s.atribuicao.parametro}</code>
                                                : <span className="text-white/50">Sem cloaker</span>}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-xs uppercase tracking-wide text-white/40">Modo</div>
                                        <div className="mt-0.5">{s.atribuicao.modo === "sequential" ? "Sequencial" : "Aleatório"}</div>
                                    </div>
                                    <div>
                                        <div className="text-xs uppercase tracking-wide text-white/40">Proteção</div>
                                        <div className="mt-0.5 flex items-center gap-1.5">
                                            <ShieldCheck size={14} className={s.atribuicao.protecao ? "text-[#4ade80]" : "text-white/40"} />
                                            <span className={s.atribuicao.protecao ? "text-[#4ade80]" : "text-white/50"}>
                                                {s.atribuicao.protecao ? "Cloaker ativo" : "Sem cloaker"}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* KPI cards */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                            <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-4 text-center">
                                <div className="text-xs uppercase tracking-wide text-white/40">Aprovação</div>
                                <div className="mt-1 text-2xl font-bold text-[#4ade80]">{s.aprovacao_pct}%</div>
                            </div>
                            <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-4 text-center">
                                <div className="text-xs uppercase tracking-wide text-white/40">Bloqueios</div>
                                <div className="mt-1 text-2xl font-bold text-[#f87171]">{s.bloqueio_pct}%</div>
                            </div>
                            <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-4 text-center">
                                <div className="text-xs uppercase tracking-wide text-white/40">Distribuição</div>
                                <div className="mt-1 text-2xl font-bold text-white">{num(s.distribuicao_total)}</div>
                                <div className="text-[10px] text-white/40">total decidido</div>
                            </div>
                            <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-4">
                                <div className="text-xs uppercase tracking-wide text-white/40 mb-2">Filtro de proteção</div>
                                {s.reasons.length === 0 ? (
                                    <div className="text-sm text-white/50">Sem bloqueios</div>
                                ) : (
                                    <div className="space-y-1">
                                        {s.reasons.slice(0, 3).map((r) => (
                                            <div key={r.reason} className="flex justify-between text-sm">
                                                <span className="text-white/70 truncate">{r.reason}</span>
                                                <span className="font-semibold text-[#f87171]">{num(r.count)}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Listas de bloqueios e aprovados */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-4">
                                <div className="flex items-center justify-between mb-3">
                                    <span className="text-sm font-semibold text-[#f87171]">Bloqueios</span>
                                    <span className="text-xs text-white/40">{s.blocks.length} registros</span>
                                </div>
                                <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                                    {s.blocks.length === 0 && <div className="text-sm text-white/40">Nenhum bloqueio.</div>}
                                    {s.blocks.map((b, i) => (
                                        <div key={i} className="rounded-lg border-l-2 border-[#f87171] bg-white/[0.03] px-3 py-2">
                                            <div className="flex justify-between gap-2">
                                                <span className="text-xs font-semibold text-[#f87171] uppercase truncate">{b.reason || "bloqueado"}</span>
                                                <span className="text-[11px] text-white/40 flex-shrink-0">{fmtDate(b.created_at)}</span>
                                            </div>
                                            <div className="text-[11px] text-white/50 truncate">IP: {b.ip || "—"}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-4">
                                <div className="flex items-center justify-between mb-3">
                                    <span className="text-sm font-semibold text-[#4ade80]">Aprovados</span>
                                    <span className="text-xs text-white/40">{s.approved_list.length} registros</span>
                                </div>
                                <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                                    {s.approved_list.length === 0 && <div className="text-sm text-white/40">Nenhum aprovado.</div>}
                                    {s.approved_list.map((a, i) => (
                                        <div key={i} className="rounded-lg border-l-2 border-[#4ade80] bg-white/[0.03] px-3 py-2">
                                            <div className="flex justify-between gap-2">
                                                <span className="text-xs font-semibold text-white/80 truncate">
                                                    {deviceFromUA(a.user_agent)}{a.pais ? ` · ${a.pais}` : ""}
                                                </span>
                                                <span className="text-[11px] text-white/40 flex-shrink-0">{fmtDate(a.created_at)}</span>
                                            </div>
                                            <div className="text-[11px] text-white/50 truncate">IP: {a.ip || "—"}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-end mt-5">
                            <button onClick={onClose} className="btn-secondary">Fechar</button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
